import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

/**
 * `underline` is the default because it is the house style — nineteen of the
 * thirty tab strips asked for it by name, and the eleven that did not were not
 * making a choice, they were taking whatever came out of the box. Naming the
 * common case `default` meant the odd one out was the effortless one.
 */
type TabsVariant = 'underline' | 'pill';

/**
 * The variant used to be a prop on both TabsList and every TabsTrigger, so a
 * list and its triggers could disagree — a bordered container wrapping pill
 * triggers, and nothing to catch it. TabsList now publishes the variant and
 * triggers read it, so callers set it once. The prop still overrides, for the
 * rare trigger that wants to differ.
 */
const TabsVariantContext = React.createContext<TabsVariant>('underline');

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: TabsVariant;
  }
>(({ className, variant = 'underline', ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        variant === 'underline'
          ? // Scrolls rather than clipping. A tab strip in a narrowed column —
            // beside a detail rail, say — will not always fit, and a trigger cut
            // off at the edge is a destination the user cannot reach at all.
            'flex w-full items-center gap-1 overflow-x-auto border-b border-border ' +
              '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          : 'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
        className
      )}
      {...props}
    />
  </TabsVariantContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    variant?: TabsVariant;
  }
>(({ className, variant: variantProp, ...props }, ref) => {
  // `??` short-circuits, so writing this as `variantProp ?? useContext(...)`
  // skipped the hook entirely whenever the prop was passed. A TabsTrigger
  // rendered once with a variant and once without would then change its hook
  // count between renders, which is the thing React cannot recover from.
  const contextVariant = React.useContext(TabsVariantContext);
  const variant = variantProp ?? contextVariant;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        // Base styles
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        // Variant-specific styles
        variant === 'underline'
          ? cn(
              // Underline variant (modern, like Vercel/Linear/GitHub)
              'relative px-4 py-3 text-muted-foreground',
              'hover:text-foreground hover:bg-muted/50',
              'data-[state=active]:text-foreground data-[state=active]:bg-transparent',
              // Active indicator - bottom border
              'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-transparent after:transition-colors',
              'data-[state=active]:after:bg-primary'
            )
          : cn(
              // Segmented-control variant
              'rounded-sm px-3 py-1.5',
              'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-raised'
            ),
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
